import { Avatar } from "@heroui/react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

// Array of mock users for the signup visual
const mockUsers = [{
  id: 1,
  name: "Sarah Johnson",
  avatar: "/images/signups/sarah-johnson.jpg",
  minAgo: 3
}, {
  id: 2,
  name: "Mark Thompson",
  avatar: "/images/signups/mark-thompson.jpg",
  minAgo: 5
}, {
  id: 3,
  name: "Emily Chen",
  avatar: "/images/signups/emily-chen.jpg",
  minAgo: 8
}, {
  id: 4,
  name: "David Rodriguez",
  avatar: "/images/signups/david-rodriguez.jpg",
  minAgo: 12
}, {
  id: 5,
  name: "Aisha Patel",
  avatar: "/images/signups/aisha-patel.jpg",
  minAgo: 9
}, {
  id: 6,
  name: "James Wilson",
  avatar: "/images/signups/james-wilson.jpg",
  minAgo: 6
}, {
  id: 7,
  name: "Maria Garcia",
  avatar: "/images/signups/maria-garcia.jpg",
  minAgo: 4
}, {
  id: 8,
  name: "Alex Kim",
  avatar: "/images/signups/alex-kim.jpg",
  minAgo: 2
}, {
  id: 9,
  name: "Lisa Taylor",
  avatar: "/images/signups/lisa-taylor.jpg",
  minAgo: 8
}, {
  id: 10,
  name: "Omar Hassan",
  avatar: "/images/signups/omar-hassan.jpg",
  minAgo: 7
}];

// Fallback avatar in case the image doesn't load
const fallbackAvatars = ["https://i.pravatar.cc/150?u=a042581f4e29026704d", "https://i.pravatar.cc/150?u=a042581f4e29026704e", "https://i.pravatar.cc/150?u=a042581f4e29026704f", "https://i.pravatar.cc/150?u=a042581f4e29026704g"];

// Animation variants for smoother transitions
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

// Static variants for when not in view (no animations)
const staticContainerVariants = {
  hidden: {},
  visible: {}
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  }
};

// UsersSignupVisual component to show animated list of users signing up
export const UsersSignupVisualTwo = ({
  isInView = false
}: {
  isInView?: boolean;
}) => {
  const [visibleUsers, setVisibleUsers] = useState<Array<(typeof mockUsers)[0]>>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const maxUsers = 5; // Maximum number of visible users at once

  // Performance optimization: Only run animations and intervals when component is in view
  useEffect(() => {
    // Initial set of users
    setVisibleUsers(mockUsers.slice(0, maxUsers));
    setCurrentIndex(maxUsers); // Start from index 5 for the next user

    // Only run interval when component is in view
    if (!isInView) return;

    // Interval to simulate new users signing up
    const interval = setInterval(() => {
      setVisibleUsers(prevUsers => {
        // Remove the first user (from top) and add a new one at the bottom
        const newUsers = [...prevUsers.slice(1)];
        return newUsers;
      });
      setCurrentIndex(prevIndex => {
        const nextUser = {
          ...mockUsers[prevIndex % mockUsers.length],
          id: Date.now() // Ensure unique IDs by adding timestamp
        };
        setVisibleUsers(prevUsers => [...prevUsers, nextUser]);
        return prevIndex + 1; // Move to next user in sequence
      });
    }, 3000); // New user every 3 seconds

    return () => clearInterval(interval);
  }, [isInView]); // Add isInView as dependency

  return <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-2 " data-sentry-component="UsersSignupVisualTwo" data-sentry-source-file="users-signup-visual-2.tsx">
      <div className="relative w-full max-w-xs">
        <div className="absolute inset-x-0 top-0 z-10 h-16 " />

        <motion.div className="flex min-h-[400px] flex-col items-center gap-3 overflow-hidden py-4" variants={isInView ? containerVariants : staticContainerVariants} initial="hidden" animate="visible" data-sentry-element="unknown" data-sentry-source-file="users-signup-visual-2.tsx">
          {isInView ? <AnimatePresence mode="popLayout" initial={false}>
              {visibleUsers.map((user, index) => <motion.div key={user.id} variants={itemVariants} initial="hidden" animate="visible" exit="exit" layout className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 bg-white p-3 dark:border-neutral-700 dark:bg-neutral-800">
                  <Avatar src={user.avatar || fallbackAvatars[index % fallbackAvatars.length]} showFallback className="flex-shrink-0" />
                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white">
                      {user.name}
                    </p>
                    <p className="text-xs text-neutral-500">
                      Just signed up {user.minAgo} min ago
                    </p>
                  </div>
                  <div className="ml-auto">
                    <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                      New
                    </span>
                  </div>
                </motion.div>)}
            </AnimatePresence> :
        // Static rendering when not in view - no animations
        visibleUsers.map((user, index) => <div key={user.id} className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 bg-white p-3 dark:border-neutral-700 dark:bg-neutral-800">
                <Avatar src={user.avatar || fallbackAvatars[index % fallbackAvatars.length]} showFallback className="flex-shrink-0" />
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                    {user.name}
                  </p>
                  <p className="text-xs text-neutral-500">
                    Just signed up {user.minAgo} min ago
                  </p>
                </div>
                <div className="ml-auto">
                  <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                    New
                  </span>
                </div>
              </div>)}
        </motion.div>

        <div className="absolute inset-x-0 bottom-0 z-10 h-16 " />
      </div>
    </div>;
};